import React from 'react'

import { connect } from 'react-redux'
import { Navigate, Route, Routes } from 'react-router-dom'
import styled from 'styled-components'

import { lazyWithVersionCheck } from '@libs/typescript-utils'

import { hasActiveSubscription } from '@helpers'

import CompanyDialogsController from '@components/CompanyDialogsController'
import { PrivateNavbar } from '@components/navigations'
import { PageLoadingErrorView, PageLoadingView } from '@components/ui'
import { InitializeUser } from '@oldComponents/InitializeUser'

import { generateMaxHeightBreakPoint } from '@constants'

import DashboardCompanyRoutes from './DashboardCompanyRoutes'
import { DashboardErrorBoundary } from './DashboardErrorBoundary'
import { NavigateToDefaultRoute } from './NavigateToDefaultRoute'

// code splitting - async component import
const AsyncDashboardProfilePage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardProfilePage'),
  PageLoadingErrorView
)
const AsyncNotFoundPrivatePage = lazyWithVersionCheck(
  () => import('@components/pages/NotFoundPage/NotFoundPrivatePage'),
  PageLoadingErrorView
)
const AsyncDashboardNoCompanyPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardNoCompanyPage'),
  PageLoadingErrorView
)
const AsyncDashboardSelectCompanyPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardSelectCompanyPage'),
  PageLoadingErrorView
)

const WrapperDiv = styled.div`
  min-height: 500px;
  color: ${({ theme }) => theme.colors.common.offBlack};
  padding-top: ${({ theme }) => theme.appBarHeight}px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  ${generateMaxHeightBreakPoint('medium')} {
    padding-top: ${({ theme }) => theme.appBarHeightMedium}px;
  }
`

interface DashboardRoutesProps {
  company: Nullable<Company>
  hasSubscription: boolean
  isCompanySelectRouteAvailable: boolean
  isMaintenanceMode: boolean
  isUserDetailsFetched: boolean
}

function PureDashboardRoutes({
  company,
  hasSubscription,
  isCompanySelectRouteAvailable,
  isMaintenanceMode,
  isUserDetailsFetched,
}: DashboardRoutesProps) {
  return isUserDetailsFetched ? (
    <WrapperDiv>
      <PrivateNavbar hasSubscription={hasSubscription} withoutLinks={isMaintenanceMode} withSupportLauncher />
      <DashboardErrorBoundary>
        <React.Suspense fallback={<PageLoadingView />}>
          <Routes>
            <Route index element={<NavigateToDefaultRoute />} />
            <Route path="profile" element={<AsyncDashboardProfilePage />} />
            <Route path="no-company" element={<AsyncDashboardNoCompanyPage />} />
            <Route
              path="select-company"
              element={
                isCompanySelectRouteAvailable ? (
                  <AsyncDashboardSelectCompanyPage />
                ) : (
                  <Navigate to="no-company" replace />
                )
              }
            />
            <Route
              path=":company_id/*"
              element={<DashboardCompanyRoutes company={company} hasSubscription={hasSubscription} />}
            />
            <Route path="*" element={<AsyncNotFoundPrivatePage />} />
          </Routes>
        </React.Suspense>
      </DashboardErrorBoundary>
      <CompanyDialogsController />
    </WrapperDiv>
  ) : (
    <InitializeUser />
  )
}

export const DashboardRoutes = connect((state: Store) => ({
  company: state.auth.company.data,
  hasSubscription: hasActiveSubscription(state),
  isCompanySelectRouteAvailable: Boolean(state.auth.companies.length),
  isMaintenanceMode: state.auth.company.isMaintenanceMode,
  isUserDetailsFetched: Boolean(state.auth.user),
}))(PureDashboardRoutes)

DashboardRoutes.displayName = 'DashboardRoutes'
